import { useState } from "react";
import { Loader2, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, fmtBRL, fmtDate, fmtPhone, labelOf, trackEvent, CLASSIFICATION_LABEL, STATUS_LABEL, formatApiError } from "@/lib/api";

const CLASS_STYLE = {
  quente: "bg-green-100 text-green-700",
  morno: "bg-amber-100 text-amber-700",
  frio: "bg-slate-200 text-slate-600",
  contato: "bg-brand-100 text-brand-700",
};

const BREAKDOWN_LABEL = {
  parcela: "Parcela compatível",
  prazo: "Prazo de compra",
  lance: "Lance / entrada",
  carta: "Carta na faixa",
  engajamento: "Conhecimento / engajamento",
};

export default function LeadDetail({ lead, onRefresh, onClose }) {
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState(lead.notes || "");
  const [notesSaving, setNotesSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const catWord = lead.category === "imovel" ? "imóvel" : lead.category === "veiculo" ? "veículo" : "consórcio";
  const waHref = `https://wa.me/55${String(lead.whatsapp).replace(/\D/g, "").slice(-11)}?text=${encodeURIComponent(
    `Olá, ${lead.name}! Aqui é a Isabela, da Porto Seguro. Vi que você tem interesse em consórcio de ${catWord}. Posso te ajudar com uma simulação?`
  )}`;

  async function changeStatus(status) {
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      toast.success("Status atualizado!");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao atualizar status."));
    } finally {
      setSaving(false);
    }
  }

  async function saveNotes() {
    if (notesSaving) return;
    setNotesSaving(true);
    try {
      await api.patch(`/admin/leads/${lead.id}`, { notes });
      toast.success("Anotações salvas!");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao salvar anotações."));
    } finally {
      setNotesSaving(false);
    }
  }

  async function removeLead() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Lead excluído definitivamente (LGPD).");
      onClose();
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao excluir."));
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <div className="space-y-6 pt-6" data-testid="lead-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-2xl font-bold text-slate-900" data-testid="lead-detail-name">{lead.name}</h2>
            {lead.status === "novo" && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-extrabold uppercase text-red-600">Novo</span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-slate-500">
            {fmtDate(lead.created_at)} · origem: {lead.source === "chat" ? "chatbot" : "formulário"}
          </p>
        </div>
        <span className={`rounded-full px-3 py-1.5 text-xs font-bold capitalize ${CLASS_STYLE[lead.classification] || "bg-slate-100"}`}>
          {CLASSIFICATION_LABEL[lead.classification] || lead.classification}
        </span>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="lead-detail-whatsapp-button"
          onClick={() => trackEvent("wa_click", { origin: "admin" })}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-wa px-5 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90"
        >
          <MessageCircle className="h-4 w-4" /> Conversar no WhatsApp: {fmtPhone(lead.whatsapp)}
        </a>
        <div className="inline-flex items-center gap-2">
          <Select value={lead.status} onValueChange={changeStatus} disabled={saving}>
            <SelectTrigger data-testid="lead-status-select" className="w-full min-h-12 rounded-full bg-white sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABEL).map(([v, l]) => (
                <SelectItem key={v} value={v}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
      </div>

      {lead.score !== null && lead.score !== undefined && (
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="lead-breakdown">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Score</p>
            <p className="font-display text-2xl font-extrabold text-slate-900">
              {lead.score}<span className="text-sm font-bold text-slate-400">/100</span>
            </p>
          </div>
          {lead.breakdown &&
            Object.entries(BREAKDOWN_LABEL).map(([k, label]) => {
              const b = lead.breakdown[k] || { points: 0, max: 0 };
              return (
                <div key={k} className="mt-3">
                  <div className="flex justify-between text-xs font-semibold text-slate-500">
                    <span>{label}</span>
                    <span>{b.points}/{b.max}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-brand-600" style={{ width: `${b.max ? (100 * b.points) / b.max : 0}%` }} />
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="lead-detail-data">
        {[
          ["Categoria", `${labelOf(lead.category)} · ${labelOf(lead.subtype)}`],
          ["Carta de crédito", fmtBRL(lead.credit_value)],
          ["Parcela planejada", fmtBRL(lead.monthly_payment)],
          ["Lance / entrada", labelOf(lead.down_payment)],
          ["FGTS / lance", lead.category === "imovel" ? labelOf(lead.fgts) : "—"],
          ["Prazo", labelOf(lead.timeline)],
          ["Conhece consórcio", labelOf(lead.knowledge)],
          ["Status atual", STATUS_LABEL[lead.status] || lead.status],
        ].map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white p-3.5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{k}</p>
            <p className="mt-0.5 text-sm font-bold text-slate-800">{v}</p>
          </div>
        ))}
      </div>

      <div data-testid="lead-notes">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Anotações da Isabela</p>
        <Textarea
          data-testid="lead-notes-input"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex.: ligar quinta às 14h, cliente pediu simulação de 300 mil..."
        />
        <button
          data-testid="lead-notes-save"
          onClick={saveNotes}
          disabled={notesSaving || notes === (lead.notes || "")}
          className="mt-2 rounded-full bg-brand-600 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
        >
          {notesSaving ? "Salvando..." : "Salvar anotações"}
        </button>
      </div>

      <div data-testid="lead-detail-transcript">
        <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">Conversa completa</p>
        <div className="chat-scroll max-h-96 space-y-2.5 overflow-y-auto rounded-3xl border border-slate-200 bg-slate-50 p-4">
          {(lead.transcript || []).map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-base leading-relaxed ${
                  m.role === "user" ? "bg-brand-600 text-white" : "border border-slate-200 bg-white text-slate-700"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {(!lead.transcript || lead.transcript.length === 0) && (
            <p className="py-6 text-center text-sm text-slate-400">Sem conversa registrada.</p>
          )}
        </div>
      </div>

      <div className="rounded-3xl border border-red-100 bg-red-50 p-4" data-testid="lead-delete-area">
        <p className="text-sm font-bold text-red-700">Excluir este lead (LGPD)</p>
        <p className="mt-1 text-xs leading-relaxed text-red-600/80">
          Apaga definitivamente os dados deste titular. Confirme a ação antes de apagar.
        </p>
        <button
          data-testid="lead-delete-button"
          onClick={removeLead}
          disabled={deleting}
          className={`mt-3 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-colors ${
            confirmDelete ? "bg-red-600 text-white hover:bg-red-700" : "border border-red-300 bg-white text-red-600 hover:bg-red-100"
          }`}
        >
          <Trash2 className="h-4 w-4" /> {deleting ? "Excluindo..." : confirmDelete ? "Confirmar exclusão definitiva" : "Excluir lead"}
        </button>
      </div>
    </div>
  );
}
